
"use client"

import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { baseUrl } from "../config/baseUrl";
import axios from "axios";
import toastify from "../config/toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faEdit,
  faSave,
  faGlobe,
  faBook,
  faQuestionCircle,
  faCalculator,
  faFileText,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import JoditEditor from "jodit-react";
import "katex/dist/katex.min.css";
import editorConfig from "../utils/editorConfig";
import { hasLatexContent, renderLatexInHtml } from "../utils/latexPreview";

export default function EditQuestion() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formval, setFormval] = useState({
    // English fields
    question_english: "",
    option_a_english: "",
    option_b_english: "",
    option_c_english: "",
    option_d_english: "",
    option_e_english: "",
    explanation_english: "",
    passage_english: "", // Add passage field
    // Hindi fields
    question_hindi: "",
    option_A_hindi: "",
    option_B_hindi: "",
    option_C_hindi: "",
    option_D_hindi: "",
    option_E_hindi: "",
    explanation_hindi: "",
    passage_hindi: "", // Add passage field
    // Bengali fields
    question_bengali: "",
    option_1_bengali: "",
    option_2_bengali: "",
    option_3_bengali: "",
    option_4_bengali: "",
    option_e_bengali: "",
    explanation_bengali: "",
    passage_bengali: "", // Add passage field
    correct_answer: "",
    subject: "",
    topic: "",
    status: "",
    question_type: "standard", // Initialize with default value
  });
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [activeLanguage, setActiveLanguage] = useState("en");
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [questionNotFound, setQuestionNotFound] = useState(false);
  const [mathExpression, setMathExpression] = useState("");
  const [showMathEditor, setShowMathEditor] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const richTextEditorConfig = useMemo(
    () => ({
      ...editorConfig,
      height: 320,
      placeholder: "Start typing here...",
    }),
    [],
  );

  // Fetch question data and other required data when component mounts
  useEffect(() => {
    const fetchData = async () => {
      setFetchingData(true);
      try {
        // Fetch all required data in parallel
        const [questionsResponse, subjectsResponse, topicsResponse] =
          await Promise.all([
            axios.get(`${baseUrl}/question/question-view/${id}`, {
              headers: {
                Authorization: localStorage.getItem("token"),
              },
            }),
            axios.get(`${baseUrl}/subject/list`, {
              headers: {
                Authorization: localStorage.getItem("token"),
              },
            }),
            axios.get(`${baseUrl}/topic/list`, {
              headers: {
                Authorization: localStorage.getItem("token"),
              },
            }),
          ]);

        // Set subjects and topics
        if (subjectsResponse.data && subjectsResponse.data.data) {
          setSubjects(subjectsResponse.data.data);
        }
        if (topicsResponse.data && topicsResponse.data.data) {
          setTopics(topicsResponse.data.data);
        }

        // Find and set the specific question
        if (questionsResponse.data.status && questionsResponse.data.data) {
          const currentQuestion = questionsResponse.data.data;
          // const currentQuestion = questions.find((q) => q._id === id);

          if (currentQuestion) {
            // Map the question data to form fields
            setFormval({
              // English fields
              question_english: currentQuestion.question_english || "",
              option_a_english: currentQuestion.option_a_english || "",
              option_b_english: currentQuestion.option_b_english || "",
              option_c_english: currentQuestion.option_c_english || "",
              option_d_english: currentQuestion.option_d_english || "",
              option_e_english: currentQuestion.option_e_english || "",
              explanation_english: currentQuestion.explanation_english || "",
              passage_english: currentQuestion.passage_english || "",
              // Hindi fields
              question_hindi: currentQuestion.question_hindi || "",
              option_A_hindi: currentQuestion.option_A_hindi || "",
              option_B_hindi: currentQuestion.option_B_hindi || "",
              option_C_hindi: currentQuestion.option_C_hindi || "",
              option_D_hindi: currentQuestion.option_D_hindi || "",
              option_E_hindi: currentQuestion.option_E_hindi || "",
              explanation_hindi: currentQuestion.explanation_hindi || "",
              passage_hindi: currentQuestion.passage_hindi || "",
              // Bengali fields
              question_bengali: currentQuestion.question_bengali || "",
              option_1_bengali: currentQuestion.option_1_bengali || "",
              option_2_bengali: currentQuestion.option_2_bengali || "",
              option_3_bengali: currentQuestion.option_3_bengali || "",
              option_4_bengali: currentQuestion.option_4_bengali || "",
              option_e_bengali: currentQuestion.option_e_bengali || "",
              explanation_bengali: currentQuestion.explanation_bengali || "",
              passage_bengali: currentQuestion.passage_bengali || "",
              // Common fields
              correct_answer: currentQuestion.correct_answer || "",
              subject: currentQuestion.subject || "",
              topic: currentQuestion.topic || "",
              status: currentQuestion.status || "",
              question_type: currentQuestion.question_type || "standard", // Set question type from API
            });
          } else {
            setQuestionNotFound(true);
            toastify.error("Question not found");
          }
        } else {
          toastify.error("Failed to fetch questions");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toastify.error("Failed to load question data");
        setQuestionNotFound(true);
      } finally {
        setFetchingData(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  const changeHandler = (e) => {
    const { name, value } = e.target;
    setFormval((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "subject" && { topic: "" }), // Reset topic when subject changes
    }));
  };

  const handleEditorChange = (content, fieldName) => {
    setFormval((prev) => ({
      ...prev,
      [fieldName]: content,
    }));
  };

  // Load KaTeX for math rendering
  useEffect(() => {
    const katexCSS = document.createElement("link");
    katexCSS.rel = "stylesheet";
    katexCSS.href =
      "https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.css";
    document.head.appendChild(katexCSS);

    const katexScript = document.createElement("script");
    katexScript.src =
      "https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.js";
    document.head.appendChild(katexScript);

    return () => {
      if (document.head.contains(katexCSS)) {
        document.head.removeChild(katexCSS);
      }
      if (document.head.contains(katexScript)) {
        document.head.removeChild(katexScript);
      }
    };
  }, []);

  const insertMathExpression = (expression) => {
    if (activeField && window.katex) {
      try {
        // Validate the expression
        window.katex.renderToString(expression, {
          throwOnError: true,
          displayMode: false,
        });

        // Get current value from the active field
        const currentValue = formval[activeField] || "";

        // Insert the math expression into the selected rich-text field
        const newValue = currentValue + ` $$${expression}$$ `;

        // Update the form value
        setFormval((prev) => ({
          ...prev,
          [activeField]: newValue,
        }));

        setMathExpression("");
        setShowMathEditor(false);
        setActiveField(null); // Clear active field
        toastify.success("Math expression inserted successfully");
      } catch (error) {
        toastify.error("Invalid math expression");
      }
    } else {
      toastify.error("Please select a field first");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Form validation
    if (!formval.question_english) {
      setLoading(false);
      return toastify.error("English question is required");
    }

    if (
      !formval.option_a_english ||
      !formval.option_b_english ||
      !formval.option_c_english ||
      !formval.option_d_english
    ) {
      setLoading(false);
      return toastify.error("All English options (A-D) are required");
    }

    if (!formval.correct_answer) {
      setLoading(false);
      return toastify.error("Correct answer is required");
    }

    if (!formval.subject) {
      setLoading(false);
      return toastify.error("Subject is required");
    }

    if (!formval.topic) {
      setLoading(false);
      return toastify.error("Topic is required");
    }

    if (!formval.status) {
      setLoading(false);
      return toastify.error("Status is required");
    }

    try {
      const response = await axios.patch(`${baseUrl}/question/${id}`, formval, {
        headers: {
          Authorization: localStorage.getItem("token"),
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200) {
        toastify.success("Question updated successfully");
        navigate(-1);
      } else {
        toastify.error(
          response.data.message ||
          "An error occurred while updating the question"
        );
      }
    } catch (error) {
      console.error("Error:", error);
      toastify.error(
        error.response?.data?.message || "Failed to update question"
      );
    } finally {
      setLoading(false);
    }
  };

  const languages = [
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "hi", name: "हिंदी", flag: "🇮🇳" },
    { code: "bn", name: "বাংলা", flag: "🇧🇩" },
  ];

  const getFieldNames = (lang) => {
    switch (lang) {
      case "en":
        return {
          question: "question_english",
          optionA: "option_a_english",
          optionB: "option_b_english",
          optionC: "option_c_english",
          optionD: "option_d_english",
          optionE: "option_e_english",
          explanation: "explanation_english",
          passage: "passage_english", // Add passage field
        };
      case "hi":
        return {
          question: "question_hindi",
          optionA: "option_A_hindi",
          optionB: "option_B_hindi",
          optionC: "option_C_hindi",
          optionD: "option_D_hindi",
          optionE: "option_E_hindi",
          explanation: "explanation_hindi",
          passage: "passage_hindi", // Add passage field
        };
      case "bn":
        return {
          question: "question_bengali",
          optionA: "option_1_bengali",
          optionB: "option_2_bengali",
          optionC: "option_3_bengali",
          optionD: "option_4_bengali",
          optionE: "option_e_bengali",
          explanation: "explanation_bengali",
          passage: "passage_bengali", // Add passage field
        };
      default:
        return {};
    }
  };

  // Filter topics based on selected subject
  const filteredTopics = formval.subject
    ? topics.filter((topic) => topic?.subject?._id === formval.subject)
    : topics;

  // Loading state
  if (fetchingData) {
    return (
      <section className="main-sec">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading question data...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Question not found state
  if (questionNotFound) {
    return (
      <section className="main-sec">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="cards">
                <div className="text-center py-5">
                  <h5>Question Not Found</h5>
                  <p>
                    The question you're looking for doesn't exist or has been
                    deleted.
                  </p>
                  <Link to="/questions" className="btn btn-info text-white">
                    <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                    Back to Questions
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="main-sec">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="dashboard-title">
                <h4 className="dash-head">
                  <FontAwesomeIcon icon={faEdit} className="me-2" />
                  Edit Question
                </h4>
              </div>
              <div className="custom-bredcump">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/">Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/questions">Questions</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Edit Question
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
            <div className="col-lg-6 d-flex justify-content-end">
              <Link to={-1} className="btn btn-info text-white">
                <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                Back
              </Link>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-12">
              <div className="cards edit-usr" style={{ position: "relative" }}>
                <form onSubmit={handleSubmit}>
                  {/* Question Type Selection */}
                  <div className="question-type-tabs">
                    <div
                      className={`question-type-tab ${formval.question_type === "standard" ? "active" : ""
                        }`}
                      onClick={() =>
                        setFormval((prev) => ({
                          ...prev,
                          question_type: "standard",
                        }))
                      }
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} />
                      <span>Standard Question</span>
                    </div>
                    <div
                      className={`question-type-tab ${formval.question_type === "math" ? "active" : ""
                        }`}
                      onClick={() =>
                        setFormval((prev) => ({
                          ...prev,
                          question_type: "math",
                        }))
                      }
                    >
                      <FontAwesomeIcon icon={faCalculator} />
                      <span>Math Question</span>
                    </div>
                    <div
                      className={`question-type-tab ${formval.question_type === "passage" ? "active" : ""
                        }`}
                      onClick={() =>
                        setFormval((prev) => ({
                          ...prev,
                          question_type: "passage",
                        }))
                      }
                    >
                      <FontAwesomeIcon icon={faFileText} />
                      <span>Passage Question</span>
                    </div>
                  </div>

                  {/* Metadata Section */}
                  <div className="metadata-section">
                    <div className="metadata-title">
                      <FontAwesomeIcon icon={faBook} />
                      Question Metadata
                    </div>
                    <div className="row">
                      <div className="col-lg-4 mb-3">
                        <label htmlFor="subject" className="form-label">
                          Subject<sup className="text-danger">*</sup>
                        </label>
                        <select
                          className="form-control"
                          name="subject"
                          onChange={changeHandler}
                          value={formval?.subject}
                          disabled={fetchingData}
                        >
                          <option value="">Select Subject</option>
                          {subjects.map((subject) => (
                            <option key={subject._id} value={subject._id}>
                              {subject.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-lg-4 mb-3">
                        <label htmlFor="topic" className="form-label">
                          Topic<sup className="text-danger">*</sup>
                        </label>
                        <select
                          className="form-control"
                          name="topic"
                          onChange={changeHandler}
                          value={formval?.topic}
                          disabled={fetchingData || !formval.subject}
                        >
                          <option value="">Select Topic</option>
                          {filteredTopics.map((topic) => (
                            <option key={topic._id} value={topic._id}>
                              {topic.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-lg-4 mb-3">
                        <label htmlFor="status" className="form-label">
                          Status<sup className="text-danger">*</sup>
                        </label>
                        <select
                          className="form-control"
                          name="status"
                          onChange={changeHandler}
                          value={formval?.status}
                        >
                          <option value="">Select Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Passage Section - Only show for passage type */}
                  {formval.question_type === "passage" && (
                    <div className="passage-section">
                      <div className="passage-title">
                        <FontAwesomeIcon icon={faFileText} />
                        Reading Passage
                      </div>
                      {/* Language Tabs for Passage */}
                      <div className="language-tabs">
                        {languages.map((lang) => (
                          <div
                            key={lang.code}
                            className={`language-tab ${activeLanguage === lang.code ? "active" : ""
                              }`}
                            onClick={() => setActiveLanguage(lang.code)}
                          >
                            <span>{lang.flag}</span>
                            <span>{lang.name}</span>
                            <FontAwesomeIcon icon={faGlobe} />
                          </div>
                        ))}
                      </div>
                      {/* Passage Content for each language */}
                      {languages.map((lang) => {
                        const fields = getFieldNames(lang.code);
                        return (
                          <div
                            key={lang.code}
                            className={`language-content ${activeLanguage === lang.code ? "active" : ""
                              }`}
                          >
                            <div className="mb-4">
                              <label className="form-label">
                                Passage ({lang.name})
                                {lang.code === "en" && (
                                  <sup className="text-danger">*</sup>
                                )}
                              </label>
                              <div className="quill-container">
                                <JoditEditor
                                  value={formval[fields.passage] || ""}
                                  config={richTextEditorConfig}
                                  tabIndex={1}
                                  onBlur={() => {}}
                                  onChange={(content) =>
                                    handleEditorChange(content, fields.passage)
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Language Tabs */}
                  <div className="language-tabs">
                    {languages.map((lang) => (
                      <div
                        key={lang.code}
                        className={`language-tab ${activeLanguage === lang.code ? "active" : ""
                          }`}
                        onClick={() => setActiveLanguage(lang.code)}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                        <FontAwesomeIcon icon={faGlobe} />
                      </div>
                    ))}
                  </div>

                  {/* Language Content */}
                  {languages.map((lang) => {
                    const fields = getFieldNames(lang.code);
                    return (
                      <div
                        key={lang.code}
                        className={`language-content ${activeLanguage === lang.code ? "active" : ""
                          }`}
                      >
                        <div className="row">
                          <div className="col-lg-12 mb-4">
                            <label
                              htmlFor={fields.question}
                              className="form-label"
                            >
                              Question ({lang.name})
                              {lang.code === "en" && (
                                <sup className="text-danger">*</sup>
                              )}
                            </label>
                            {formval.question_type === "math" && (
                              <div className="math-toolbar">
                                <button
                                  type="button"
                                  className="math-btn"
                                  onClick={() => {
                                    setActiveField(fields.question);
                                    setShowMathEditor(true);
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faCalculator}
                                    className="me-1"
                                  />
                                  Add Math
                                </button>
                              </div>
                            )}
                            <div className="quill-container">
                              <JoditEditor
                                value={formval[fields.question] || ""}
                                config={richTextEditorConfig}
                                tabIndex={1}
                                onBlur={() => {}}
                                onChange={(content) =>
                                  handleEditorChange(content, fields.question)
                                }
                              />
                            </div>
                            {hasLatexContent(formval[fields.question]) && (
                                <div className="mt-2">
                                  <span>Preview </span>
                                  <div
                                    className="latex-preview form-control mt-2"
                                    dangerouslySetInnerHTML={{
                                      __html: renderLatexInHtml(
                                        formval[fields.question]
                                      ),
                                    }}
                                  />
                                </div>
                              )}
                          </div>
                          <div className="col-lg-6 mb-4">
                            <div className="option-container">
                              <span className="option-label">
                                Option A ({lang.name})
                              </span>
                              {formval.question_type === "math" && (
                                <div className="math-toolbar">
                                  <button
                                    type="button"
                                    className="math-btn"
                                    onClick={() => {
                                      setActiveField(fields.optionA);
                                      setShowMathEditor(true);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faCalculator} />
                                  </button>
                                </div>
                              )}
                              <input
                                type="text"
                                className="form-control"
                                name={fields.optionA}
                                placeholder={`Enter option A in ${lang.name}`}
                                onChange={changeHandler}
                                value={formval[fields.optionA]}
                              />
                              {hasLatexContent(formval[fields.optionA]) && (
                                  <div className="mt-2">
                                    <span>Preview </span>
                                    <div
                                      className="latex-preview form-control mt-2"
                                      dangerouslySetInnerHTML={{
                                        __html: renderLatexInHtml(
                                          formval[fields.optionA]
                                        ),
                                      }}
                                    />
                                  </div>
                                )}
                            </div>
                          </div>
                          <div className="col-lg-6 mb-4">
                            <div className="option-container">
                              <span className="option-label">
                                Option B ({lang.name})
                              </span>
                              {formval.question_type === "math" && (
                                <div className="math-toolbar">
                                  <button
                                    type="button"
                                    className="math-btn"
                                    onClick={() => {
                                      setActiveField(fields.optionB);
                                      setShowMathEditor(true);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faCalculator} />
                                  </button>
                                </div>
                              )}
                              <input
                                type="text"
                                className="form-control"
                                name={fields.optionB}
                                placeholder={`Enter option B in ${lang.name}`}
                                onChange={changeHandler}
                                value={formval[fields.optionB]}
                              />
                              {hasLatexContent(formval[fields.optionB]) && (
                                  <div className="mt-2">
                                    <span>Preview </span>
                                    <div
                                      className="latex-preview form-control mt-2"
                                      dangerouslySetInnerHTML={{
                                        __html: renderLatexInHtml(
                                          formval[fields.optionB]
                                        ),
                                      }}
                                    />
                                  </div>
                                )}
                            </div>
                          </div>
                          <div className="col-lg-6 mb-4">
                            <div className="option-container">
                              <span className="option-label">
                                Option C ({lang.name})
                              </span>
                              {formval.question_type === "math" && (
                                <div className="math-toolbar">
                                  <button
                                    type="button"
                                    className="math-btn"
                                    onClick={() => {
                                      setActiveField(fields.optionC);
                                      setShowMathEditor(true);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faCalculator} />
                                  </button>
                                </div>
                              )}
                              <input
                                type="text"
                                className="form-control"
                                name={fields.optionC}
                                placeholder={`Enter option C in ${lang.name}`}
                                onChange={changeHandler}
                                value={formval[fields.optionC]}
                              />
                              {hasLatexContent(formval[fields.optionC]) && (
                                  <div className="mt-2">
                                    <span>Preview </span>
                                    <div
                                      className="latex-preview form-control mt-2"
                                      dangerouslySetInnerHTML={{
                                        __html: renderLatexInHtml(
                                          formval[fields.optionC]
                                        ),
                                      }}
                                    />
                                  </div>
                                )}
                            </div>
                          </div>
                          <div className="col-lg-6 mb-4">
                            <div className="option-container">
                              <span className="option-label">
                                Option D ({lang.name})
                              </span>
                              {formval.question_type === "math" && (
                                <div className="math-toolbar">
                                  <button
                                    type="button"
                                    className="math-btn"
                                    onClick={() => {
                                      setActiveField(fields.optionD);
                                      setShowMathEditor(true);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faCalculator} />
                                  </button>
                                </div>
                              )}
                              <input
                                type="text"
                                className="form-control"
                                name={fields.optionD}
                                placeholder={`Enter option D in ${lang.name}`}
                                onChange={changeHandler}
                                value={formval[fields.optionD]}
                              />
                              {hasLatexContent(formval[fields.optionD]) && (
                                  <div className="mt-2">
                                    <span>Preview </span>
                                    <div
                                      className="latex-preview form-control mt-2"
                                      dangerouslySetInnerHTML={{
                                        __html: renderLatexInHtml(
                                          formval[fields.optionD]
                                        ),
                                      }}
                                    />
                                  </div>
                                )}
                            </div>
                          </div>
                          <div className="col-lg-12 mb-4">
                            <div className="option-container">
                              <span className="option-label">
                                Option E ({lang.name}) - Optional
                              </span>
                              {formval.question_type === "math" && (
                                <div className="math-toolbar">
                                  <button
                                    type="button"
                                    className="math-btn"
                                    onClick={() => {
                                      setActiveField(fields.optionE);
                                      setShowMathEditor(true);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faCalculator} />
                                  </button>
                                </div>
                              )}
                              <input
                                type="text"
                                className="form-control"
                                name={fields.optionE}
                                placeholder={`Enter option E in ${lang.name} (optional)`}
                                onChange={changeHandler}
                                value={formval[fields.optionE]}
                              />
                              {hasLatexContent(formval[fields.optionE]) && (
                                  <div className="mt-2">
                                    <span>Preview </span>
                                    <div
                                      className="latex-preview form-control mt-2"
                                      dangerouslySetInnerHTML={{
                                        __html: renderLatexInHtml(
                                          formval[fields.optionE]
                                        ),
                                      }}
                                    />
                                  </div>
                                )}
                            </div>
                          </div>
                          <div className="col-lg-12 mb-4">
                            <label
                              htmlFor={fields.explanation}
                              className="form-label"
                            >
                              Explanation ({lang.name})
                            </label>
                            {formval.question_type === "math" && (
                              <div className="math-toolbar">
                                <button
                                  type="button"
                                  className="math-btn"
                                  onClick={() => {
                                    setActiveField(fields.explanation);
                                    setShowMathEditor(true);
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faCalculator}
                                    className="me-1"
                                  />
                                  Add Math
                                </button>
                              </div>
                            )}
                            <div className="quill-container">
                              <JoditEditor
                                value={formval[fields.explanation] || ""}
                                config={richTextEditorConfig}
                                tabIndex={1}
                                onBlur={() => {}}
                                onChange={(content) =>
                                  handleEditorChange(content, fields.explanation)
                                }
                              />
                              {hasLatexContent(formval[fields.explanation]) && (
                                  <div className="mt-2">
                                    <span>Preview </span>
                                    <div
                                      className="latex-preview form-control mt-2"
                                      dangerouslySetInnerHTML={{
                                        __html: renderLatexInHtml(
                                          formval[fields.explanation]
                                        ),
                                      }}
                                    />
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Correct Answer Section */}
                  <div className="correct-answer-container">
                    <div className="correct-answer-title">
                      Select Correct Answer<sup className="text-danger">*</sup>
                    </div>
                    <div className="radio-container">
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="correct_answer"
                          value="A"
                          onChange={changeHandler}
                          checked={formval.correct_answer === "A"}
                        />
                        Option A
                      </label>
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="correct_answer"
                          value="B"
                          onChange={changeHandler}
                          checked={formval.correct_answer === "B"}
                        />
                        Option B
                      </label>
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="correct_answer"
                          value="C"
                          onChange={changeHandler}
                          checked={formval.correct_answer === "C"}
                        />
                        Option C
                      </label>
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="correct_answer"
                          value="D"
                          onChange={changeHandler}
                          checked={formval.correct_answer === "D"}
                        />
                        Option D
                      </label>
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="correct_answer"
                          value="E"
                          onChange={changeHandler}
                          checked={formval.correct_answer === "E"}
                        />
                        Option E
                      </label>
                    </div>
                  </div>

                  <div className="col-lg-12 text-center mt-4">
                    <button
                      type="submit"
                      className="btn btn-for-add text-white"
                      disabled={loading || fetchingData}
                    >
                      {loading && <div className="loading-spinner"></div>}
                      <FontAwesomeIcon icon={faSave} className="me-2" />
                      {loading ? "Saving..." : "Save Question"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Math Editor Modal */}
        {showMathEditor && (
          <div className="math-editor-modal">
            <div className="math-editor-content">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Math Expression Editor</h5>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    setShowMathEditor(false);
                    setActiveField(null);
                    setMathExpression("");
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="mb-3">
                <label className="form-label">LaTeX Expression</label>
                <input
                  type="text"
                  className="form-control"
                  value={mathExpression}
                  onChange={(e) => setMathExpression(e.target.value)}
                  placeholder="Enter LaTeX expression (e.g., x^2 + y^2 = z^2)"
                />
                <small className="text-muted">
                  Active field: {activeField || "None selected"}
                </small>
              </div>
              <div className="math-symbols">
                {[
                  { symbol: "\\alpha", display: "α" },
                  { symbol: "\\beta", display: "β" },
                  { symbol: "\\gamma", display: "γ" },
                  { symbol: "\\delta", display: "δ" },
                  { symbol: "\\pi", display: "π" },
                  { symbol: "\\theta", display: "θ" },
                  { symbol: "\\lambda", display: "λ" },
                  { symbol: "\\mu", display: "μ" },
                  { symbol: "\\sum", display: "Σ" },
                  { symbol: "\\int", display: "∫" },
                  { symbol: "\\frac{a}{b}", display: "a/b" },
                  { symbol: "\\sqrt{x}", display: "√x" },
                  { symbol: "x^2", display: "x²" },
                  { symbol: "x_1", display: "x₁" },
                  { symbol: "\\sin", display: "sin" },
                  { symbol: "\\cos", display: "cos" },
                  { symbol: "\\tan", display: "tan" },
                  { symbol: "\\log", display: "log" },
                  { symbol: "\\ln", display: "ln" },
                  { symbol: "\\infty", display: "∞" },
                  { symbol: "\\pm", display: "±" },
                  { symbol: "\\times", display: "×" },
                  { symbol: "\\div", display: "÷" },
                  { symbol: "\\leq", display: "≤" },
                  { symbol: "\\geq", display: "≥" },
                  { symbol: "\\neq", display: "≠" },
                  { symbol: "\\approx", display: "≈" },
                  { symbol: "\\rightarrow", display: "→" },
                ].map((item) => (
                  <div
                    key={item.symbol}
                    className="math-symbol"
                    onClick={() =>
                      setMathExpression((prev) => prev + item.symbol)
                    }
                    title={item.symbol}
                  >
                    {item.display}
                  </div>
                ))}
              </div>
              <div className="d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowMathEditor(false);
                    setActiveField(null);
                    setMathExpression("");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => insertMathExpression(mathExpression)}
                  disabled={!mathExpression.trim() || !activeField}
                >
                  Insert Expression
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
      <style>

        <style>{`
                .main-sec {
                    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
                    min-height: 100vh;
                    padding: 2rem 0;
                }

                .cards {
                    background: white;
                    border-radius: 15px;
                    padding: 2rem;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
                    margin-bottom: 2rem;
                    position: relative;
                }

                .loading-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(255, 255, 255, 0.9);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    z-index: 10;
                    border-radius: 15px;
                }

                .loading-spinner {
                    width: 40px;
                    height: 40px;
                    border: 4px solid #f3f3f3;
                    border-top: 4px solid #7436A9;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-bottom: 1rem;
                }

                .loading-text {
                    color: #7436A9;
                    font-weight: 600;
                }

                .metadata-section {
                    background: #f8f9ff;
                    border-radius: 10px;
                    padding: 1.5rem;
                    margin-bottom: 2rem;
                    border-left: 4px solid #7436A9;
                }

                .metadata-title {
                    color: #7436A9;
                    font-weight: 700;
                    margin-bottom: 1rem;
                    font-size: 1.1rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .language-tabs {
                    display: flex;
                    gap: 0.5rem;
                    margin-bottom: 2rem;
                    border-bottom: 2px solid #eee;
                }

                .language-tab {
                    padding: 1rem 1.5rem;
                    background: #f8f9fa;
                    border: 1px solid #ddd;
                    border-bottom: none;
                    border-radius: 8px 8px 0 0;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-weight: 600;
                }

                .language-tab:hover {
                    background: #e9ecef;
                }

                .language-tab.active {
                    background: #7436A9;
                    color: white;
                    border-color: #7436A9;
                }

                .language-content {
                    display: none;
                    animation: fadeIn 0.3s ease-in;
                }

                .language-content.active {
                    display: block;
                }

                .question-textarea {
                    min-height: 120px;
                    resize: vertical;
                }

                .option-container {
                    position: relative;
                }

                .option-label {
                    position: absolute;
                    top: -8px;
                    left: 12px;
                    background: white;
                    padding: 0 8px;
                    font-size: 0.85rem;
                    color: #7436A9;
                    font-weight: 600;
                    z-index: 1;
                }

                .correct-answer-container {
                    background: #fff3cd;
                    border: 1px solid #ffeaa7;
                    border-radius: 10px;
                    padding: 1.5rem;
                    margin: 2rem 0;
                }

                .correct-answer-title {
                    font-weight: 700;
                    color: #856404;
                    margin-bottom: 1rem;
                    font-size: 1.1rem;
                }

                .radio-container {
                    display: flex;
                    gap: 2rem;
                    flex-wrap: wrap;
                }

                .radio-option {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    cursor: pointer;
                    font-weight: 600;
                    color: #495057;
                }

                .radio-option input[type="radio"] {
                    accent-color: #7436A9;
                    width: 18px;
                    height: 18px;
                }

                .form-label {
                    font-weight: 600;
                    color: #555;
                    margin-bottom: 0.5rem;
                }

                .form-control {
                    border-radius: 8px;
                    border: 1px solid #ddd;
                    padding: 0.75rem 1rem;
                    transition: all 0.3s ease;
                }

                .form-control:focus {
                    border-color: #7436A9;
                    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.25);
                }

                .btn-for-add {
                    background: linear-gradient(135deg, #7436A9 0%, #764ba2 100%);
                    border: none;
                    border-radius: 8px;
                    padding: 0.75rem 2rem;
                    font-weight: 600;
                    transition: all 0.3s ease;
                    color: white;
                }

                .btn-for-add:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
                }

                .btn-for-add:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                }

                .btn-info {
                    background: #4facfe;
                    border: none;
                    border-radius: 8px;
                    padding: 0.75rem 1.5rem;
                    font-weight: 600;
                    transition: all 0.3s ease;
                }

                .btn-info:hover {
                    background: #2f86fe;
                    transform: translateY(-2px);
                    box-shadow: 0 10px 20px rgba(79, 172, 254, 0.3);
                }

                .dash-head {
                    color: #333;
                    font-size: 1.8rem;
                    font-weight: 700;
                    margin: 0;
                }

                @keyframes spin {
                    0% {
                        transform: rotate(0deg);
                    }
                    100% {
                        transform: rotate(360deg);
                    }
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @media (max-width: 768px) {
                    .language-tabs {
                        flex-direction: column;
                    }

                    .radio-container {
                        flex-direction: column;
                        gap: 1rem;
                    }

                    .cards {
                        padding: 1.5rem;
                    }
                }
            `}</style>
      </style>
    </>
  );
}
